import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
    Button,
    IconButton,
    Tooltip,
    Chip,
    Menu,
    MenuItem,
    ListSubheader,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    InputAdornment
} from "@mui/material"
import {
    Add,
    ArrowBack,
    ArrowUpward,
    ArrowDownward,
    AttachFileOutlined,
    CheckCircle,
    CreateNewFolderOutlined,
    DeleteOutline,
    DriveFileMoveOutlined,
    EditOutlined,
    FolderOutlined,
    ForumOutlined,
    LinkOutlined,
    MoreVert,
    OpenInNew,
    PlayCircleOutline,
    SchoolOutlined,
    StarOutline,
    VideoLibraryOutlined,
    VisibilityOutlined
} from "@mui/icons-material"
import { Loader, ToastMessage } from "../../components/index"
import {
    useGetCoursesQuery,
    useEditCourseMutation,
    useGetCourseLecturesQuery,
    useGetCourseChaptersQuery,
    useCreateLectureMutation,
    useEditLectureMutation,
    useDeleteLectureMutation,
    useCreateChapterMutation,
    useEditChapterMutation,
    useDeleteChapterMutation,
    useReorderChaptersMutation,
    useReorderLecturesMutation
} from "../../Redux/actions/lecture.action"
import { isOwner } from "../../utils/hooks"
import { groupLecturesByChapter } from "../chapters"
import { managePaths } from "../constants"
import { plural } from "../components/datetime"
import { formatPrice } from "../components/price"
import { describeVideoUrl } from "../components/VideoPlayer"
import EmptyState from "../components/EmptyState"
import ConfirmDialog from "../components/ConfirmDialog"
import CourseFormDialog from "./CourseFormDialog"
import CourseReviewsDialog from "./CourseReviewsDialog"
import LectureFormDialog from "./LectureFormDialog"
import ChapterFormDialog from "./ChapterFormDialog"

const UNGROUPED = "none"

const errorText = (error, fallback) => _.get(error, "data.detail", null) || fallback

const sectionKey = (section) =>
    section.chapter ? String(section.chapter.chapter_id) : UNGROUPED

/** Swap two neighbours of a list, returning a new array. */
const swap = (list, index, nextIndex) => {
    const next = [...list]
    ;[next[index], next[nextIndex]] = [next[nextIndex], next[index]]
    return next
}

/* ------------------------------------------------------------------------ */
/* Small pieces                                                             */
/* ------------------------------------------------------------------------ */

function VideoThumb({ url, className = "" }) {
    const { thumbnail } = describeVideoUrl(url)
    const [failed, setFailed] = React.useState(false)

    return (
        <div className={`relative aspect-video shrink-0 overflow-hidden rounded-lg bg-slate-800 ${className}`}>
            {thumbnail && !failed ? (
                <img
                    src={thumbnail}
                    alt=""
                    loading="lazy"
                    onError={() => setFailed(true)}
                    className="h-full w-full object-cover"
                />
            ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900">
                    <PlayCircleOutline sx={{ color: "rgba(255,255,255,.8)", fontSize: 28 }} />
                </div>
            )}
        </div>
    )
}

VideoThumb.propTypes = { url: PropTypes.string, className: PropTypes.string }

/**
 * Inline form at the top of a chapter for adding videos one after another.
 * It stays open after each save so a whole chapter can be filled in a row.
 */
function VideoComposer({ chapterLabel, saving, onAdd, onClose }) {
    const empty = { video_url: "", title: "", duration: "", description: "", is_preview: false }
    const [form, setForm] = React.useState(empty)
    const [errors, setErrors] = React.useState({})
    const [serverError, setServerError] = React.useState("")
    const [added, setAdded] = React.useState(0)
    const urlInput = React.useRef(null)

    const video = describeVideoUrl(form.video_url)
    const setField = (field) => (event) =>
        setForm((prev) => ({ ...prev, [field]: event.target.value }))

    const submit = async (event) => {
        event.preventDefault()
        const nextErrors = {}
        const url = form.video_url.trim()
        if (!url) nextErrors.video_url = "Paste the video link"
        else if (!/^https?:\/\//i.test(url)) nextErrors.video_url = "Links start with http:// or https://"
        if (!form.title.trim()) nextErrors.title = "Give the video a title"
        setErrors(nextErrors)
        if (Object.keys(nextErrors).length) return

        setServerError("")
        const error = await onAdd({
            video_url: url,
            title: form.title.trim(),
            duration: form.duration.trim() || null,
            description: form.description.trim() || null,
            is_preview: form.is_preview
        })
        if (error) {
            setServerError(error)
            return
        }
        setAdded((count) => count + 1)
        setForm(empty)
        urlInput.current?.focus()
    }

    return (
        <form
            onSubmit={submit}
            noValidate
            className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 sm:p-5"
        >
            <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                    <p className="text-sm font-semibold text-slate-800">Add a video</p>
                    <p className="text-xs text-slate-500">
                        To {chapterLabel}
                        {added > 0 && ` · ${plural(added, "video")} added`}
                    </p>
                </div>
                <Button size="small" onClick={onClose} disabled={saving}>
                    Done
                </Button>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
                <div className="w-full sm:w-48">
                    <VideoThumb url={form.video_url} className="w-full" />
                    {video.provider && (
                        <p className="mt-1.5 flex items-center gap-1 text-xs text-slate-500">
                            <CheckCircle sx={{ fontSize: 14, color: "#16a34a" }} />
                            {video.provider}
                        </p>
                    )}
                </div>

                <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-6">
                    <TextField
                        inputRef={urlInput}
                        autoFocus
                        size="small"
                        label="Video link"
                        placeholder="YouTube, Vimeo or a direct .mp4 link"
                        value={form.video_url}
                        onChange={setField("video_url")}
                        error={Boolean(errors.video_url)}
                        helperText={errors.video_url}
                        className="sm:col-span-6"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LinkOutlined fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ bgcolor: "white" }}
                    />
                    <TextField
                        size="small"
                        label="Title"
                        value={form.title}
                        onChange={setField("title")}
                        error={Boolean(errors.title)}
                        helperText={errors.title}
                        inputProps={{ maxLength: 300 }}
                        className="sm:col-span-4"
                        sx={{ bgcolor: "white" }}
                    />
                    <TextField
                        size="small"
                        label="Duration"
                        placeholder="12:40"
                        value={form.duration}
                        onChange={setField("duration")}
                        className="sm:col-span-2"
                        sx={{ bgcolor: "white" }}
                    />
                    <TextField
                        size="small"
                        multiline
                        minRows={2}
                        label="Description (optional)"
                        value={form.description}
                        onChange={setField("description")}
                        className="sm:col-span-6"
                        sx={{ bgcolor: "white" }}
                    />
                </div>
            </div>

            {serverError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {serverError}
                </Alert>
            )}

            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={form.is_preview}
                            onChange={(event) =>
                                setForm((prev) => ({ ...prev, is_preview: event.target.checked }))
                            }
                        />
                    }
                    label={
                        <span className="text-sm text-slate-700">
                            Free preview
                            <span className="ml-1 text-xs text-slate-500">
                                (watchable before buying)
                            </span>
                        </span>
                    }
                />
                <Button type="submit" variant="contained" startIcon={<Add />} disabled={saving}>
                    {saving ? "Adding…" : "Add video"}
                </Button>
            </div>
        </form>
    )
}

VideoComposer.propTypes = {
    chapterLabel: PropTypes.string.isRequired,
    saving: PropTypes.bool,
    onAdd: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
}

function VideoRow({ lecture, number, first, last, busy, canMove, onMoveUp, onMoveDown, onMoveTo, onEdit, onDelete }) {
    return (
        <li className="group flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-slate-300 hover:shadow-sm sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="w-9 shrink-0 text-center text-xs font-bold tabular-nums text-slate-400">
                    {number}
                </span>
                <button
                    type="button"
                    onClick={onEdit}
                    className="w-24 shrink-0 sm:w-28"
                    aria-label={`Edit ${lecture.title}`}
                >
                    <VideoThumb url={lecture.video_url} className="w-full" />
                </button>
                <div className="min-w-0 flex-1">
                    <button
                        type="button"
                        onClick={onEdit}
                        className="block max-w-full truncate text-left text-sm font-semibold text-slate-800 hover:text-indigo-600"
                    >
                        {lecture.title}
                    </button>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        {lecture.duration && <span className="tabular-nums">{lecture.duration}</span>}
                        {lecture.is_preview && (
                            <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                icon={<VisibilityOutlined sx={{ fontSize: 14 }} />}
                                label="Free preview"
                                sx={{ height: 20, fontSize: 11 }}
                            />
                        )}
                        {lecture.resource_count > 0 && (
                            <span className="flex items-center gap-0.5">
                                <AttachFileOutlined sx={{ fontSize: 14 }} />
                                {plural(lecture.resource_count, "file")}
                            </span>
                        )}
                        <a
                            href={lecture.video_url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-0.5 text-indigo-600 hover:underline"
                        >
                            {describeVideoUrl(lecture.video_url).provider || "Open"}
                            <OpenInNew sx={{ fontSize: 12 }} />
                        </a>
                    </div>
                </div>
            </div>

            <div className="flex shrink-0 items-center justify-end gap-0.5">
                <Tooltip title="Move up">
                    <span>
                        <IconButton size="small" disabled={busy || first} onClick={onMoveUp}>
                            <ArrowUpward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Move down">
                    <span>
                        <IconButton size="small" disabled={busy || last} onClick={onMoveDown}>
                            <ArrowDownward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                {canMove && (
                    <Tooltip title="Move to another chapter">
                        <span>
                            <IconButton size="small" disabled={busy} onClick={onMoveTo}>
                                <DriveFileMoveOutlined fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                <Tooltip title="Edit video, files and links">
                    <IconButton size="small" onClick={onEdit}>
                        <EditOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete video">
                    <IconButton size="small" color="error" onClick={onDelete}>
                        <DeleteOutline fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>
        </li>
    )
}

VideoRow.propTypes = {
    lecture: PropTypes.object.isRequired,
    number: PropTypes.string.isRequired,
    first: PropTypes.bool,
    last: PropTypes.bool,
    busy: PropTypes.bool,
    canMove: PropTypes.bool,
    onMoveUp: PropTypes.func.isRequired,
    onMoveDown: PropTypes.func.isRequired,
    onMoveTo: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
}

/** Quick "new chapter" field at the foot of the chapter list. */
function NewChapterField({ saving, onCreate }) {
    const [title, setTitle] = React.useState("")

    const submit = async (event) => {
        event.preventDefault()
        if (!title.trim()) return
        if (await onCreate(title.trim())) setTitle("")
    }

    return (
        <form onSubmit={submit} className="flex gap-2">
            <TextField
                size="small"
                fullWidth
                placeholder="New chapter title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                inputProps={{ maxLength: 300, "aria-label": "New chapter title" }}
                sx={{ bgcolor: "white" }}
            />
            <Tooltip title="Add chapter">
                <span>
                    <IconButton
                        type="submit"
                        color="primary"
                        disabled={saving || !title.trim()}
                        sx={{ border: "1px solid", borderColor: "divider", borderRadius: "10px" }}
                    >
                        <Add fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </form>
    )
}

NewChapterField.propTypes = { saving: PropTypes.bool, onCreate: PropTypes.func.isRequired }

/* ------------------------------------------------------------------------ */
/* Page                                                                     */
/* ------------------------------------------------------------------------ */

/**
 * Owner page for building one course chapter by chapter: the chapter list on
 * the left, the selected chapter's videos on the right, and an inline form to
 * add videos to it. The selected chapter lives in the URL (?chapter=<id>, or
 * "none" for videos outside every chapter) so a refresh keeps the place.
 */
export default function CourseBuilder() {
    const { course_id: courseParam } = useParams()
    const course_id = Number(courseParam)
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const canManage = isOwner()

    const coursesQuery = useGetCoursesQuery(undefined, { skip: !canManage })
    const lecturesQuery = useGetCourseLecturesQuery({ course_id }, { skip: !canManage })
    const chaptersQuery = useGetCourseChaptersQuery({ course_id }, { skip: !canManage })

    const [editCourse, editCourseState] = useEditCourseMutation()
    const [createLecture, createLectureState] = useCreateLectureMutation()
    const [editLecture, editLectureState] = useEditLectureMutation()
    const [deleteLecture, deleteLectureState] = useDeleteLectureMutation()
    const [createChapter, createChapterState] = useCreateChapterMutation()
    const [editChapter, editChapterState] = useEditChapterMutation()
    const [deleteChapter, deleteChapterState] = useDeleteChapterMutation()
    const [reorderChapters, reorderChaptersState] = useReorderChaptersMutation()
    const [reorderLectures, reorderLecturesState] = useReorderLecturesMutation()

    const [composerOpen, setComposerOpen] = React.useState(false)
    const [editingLectureId, setEditingLectureId] = React.useState(null)
    const [chapterDialog, setChapterDialog] = React.useState(null) // {chapter}
    const [courseDialogOpen, setCourseDialogOpen] = React.useState(false)
    const [reviewsOpen, setReviewsOpen] = React.useState(false)
    const [dialogError, setDialogError] = React.useState("")
    const [pendingDelete, setPendingDelete] = React.useState(null) // {kind, item}
    const [moveMenu, setMoveMenu] = React.useState(null) // {anchor, lecture}
    const [chapterMenu, setChapterMenu] = React.useState(null) // anchor
    const [notification, setNotification] = React.useState({ show: false, type: "", message: "" })

    const notify = (type, message) => setNotification({ show: true, type, message })

    const course = React.useMemo(
        () => _.get(coursesQuery, "data.list", []).find((item) => item.course_id === course_id) || null,
        [coursesQuery, course_id]
    )
    const lectures = React.useMemo(() => _.get(lecturesQuery, "data.list", []), [lecturesQuery])
    const chapters = React.useMemo(() => _.get(chaptersQuery, "data.list", []), [chaptersQuery])
    const sections = React.useMemo(
        () => groupLecturesByChapter(chapters, lectures),
        [chapters, lectures]
    )

    const selectedKey = searchParams.get("chapter")
    const activeIndex = Math.max(0, sections.findIndex((section) => sectionKey(section) === selectedKey))
    const active = sections[activeIndex] || null
    const activeChapter = active ? active.chapter : null
    const chapterIndex = activeChapter ? chapters.findIndex((c) => c.chapter_id === activeChapter.chapter_id) : -1

    const selectSection = (key) => {
        setComposerOpen(false)
        setSearchParams({ chapter: key }, { replace: true })
    }

    const editingLecture = editingLectureId
        ? lectures.find((item) => item.lecture_id === editingLectureId) || null
        : null

    const busy =
        editCourseState.isLoading ||
        editLectureState.isLoading ||
        deleteLectureState.isLoading ||
        createChapterState.isLoading ||
        editChapterState.isLoading ||
        deleteChapterState.isLoading ||
        reorderChaptersState.isLoading ||
        reorderLecturesState.isLoading

    const loading = coursesQuery.isLoading || lecturesQuery.isLoading || chaptersQuery.isLoading

    /* ----------------------------- chapters ---------------------------- */

    const addChapter = async (title) => {
        try {
            const result = await createChapter({ course_id, title, description: null }).unwrap()
            selectSection(String(result.chapter.chapter_id))
            setComposerOpen(true)
            notify("success", `Chapter "${title}" added. Now add its videos.`)
            return true
        } catch (error) {
            notify("error", errorText(error, "Could not add the chapter."))
            return false
        }
    }

    const submitChapter = async (values) => {
        try {
            await editChapter({ chapter_id: chapterDialog.chapter.chapter_id, ...values }).unwrap()
            setChapterDialog(null)
            notify("success", "Chapter updated.")
        } catch (error) {
            setDialogError(errorText(error, "Could not save the chapter."))
        }
    }

    const moveChapter = async (step) => {
        setChapterMenu(null)
        const chapter_ids = swap(chapters.map((item) => item.chapter_id), chapterIndex, chapterIndex + step)
        try {
            await reorderChapters({ course_id, chapter_ids }).unwrap()
        } catch (error) {
            notify("error", errorText(error, "Could not reorder the chapters."))
        }
    }

    /* ------------------------------ videos ----------------------------- */

    /** Returns an error message, or null once the video is saved. */
    const addVideo = async (values) => {
        try {
            await createLecture({
                ...values,
                course_id,
                chapter_id: activeChapter ? activeChapter.chapter_id : null
            }).unwrap()
            return null
        } catch (error) {
            return errorText(error, "Could not add the video.")
        }
    }

    const submitLecture = async (values) => {
        try {
            await editLecture({ lecture_id: editingLecture.lecture_id, ...values, course_id }).unwrap()
            setEditingLectureId(null)
            notify("success", "Video updated.")
        } catch (error) {
            setDialogError(errorText(error, "Could not save the video."))
        }
    }

    /*
     * The reorder endpoint takes every lecture of the course grouped by
     * chapter. `edit` rearranges a copy of the current groups.
     */
    const saveLectureOrder = async (edit, message) => {
        const groups = sections.map(({ chapter, lectures: items }) => ({
            chapter_id: chapter ? chapter.chapter_id : null,
            lecture_ids: items.map((item) => item.lecture_id)
        }))
        edit(groups)
        try {
            await reorderLectures({
                course_id,
                groups: groups.filter((group) => group.lecture_ids.length > 0)
            }).unwrap()
            if (message) notify("success", message)
        } catch (error) {
            notify("error", errorText(error, "Could not reorder the videos."))
        }
    }

    const moveVideoWithin = (index, step) =>
        saveLectureOrder((groups) => {
            const group = groups[activeIndex]
            group.lecture_ids = swap(group.lecture_ids, index, index + step)
        })

    const moveVideoTo = (lecture, chapter_id) => {
        setMoveMenu(null)
        if ((lecture.chapter_id ?? null) === chapter_id) return
        const target = chapters.find((c) => c.chapter_id === chapter_id)
        saveLectureOrder((groups) => {
            groups.forEach((group) => {
                group.lecture_ids = group.lecture_ids.filter((id) => id !== lecture.lecture_id)
            })
            let destination = groups.find((group) => group.chapter_id === chapter_id)
            if (!destination) {
                destination = { chapter_id, lecture_ids: [] }
                groups.push(destination)
            }
            destination.lecture_ids.push(lecture.lecture_id)
        }, `Moved to ${target ? target.title : "Not in a chapter"}.`)
    }

    /* ------------------------------ course ----------------------------- */

    const submitCourse = async (values) => {
        try {
            await editCourse({ course_id, ...values }).unwrap()
            setCourseDialogOpen(false)
            notify("success", "Course updated.")
        } catch (error) {
            notify("error", errorText(error, "Could not save the course."))
        }
    }

    /* ------------------------------ delete ----------------------------- */

    const confirmDelete = async () => {
        const { kind, item } = pendingDelete
        try {
            if (kind === "chapter") {
                await deleteChapter({ chapter_id: item.chapter_id }).unwrap()
                /* Land on the chapter before the deleted one */
                const remaining = sections.filter(
                    (section) => !section.chapter || section.chapter.chapter_id !== item.chapter_id
                )
                const next = remaining[Math.max(0, activeIndex - 1)]
                if (next) selectSection(sectionKey(next))
                else setSearchParams({}, { replace: true })
                notify("success", "Chapter deleted.")
            } else {
                await deleteLecture({ lecture_id: item.lecture_id }).unwrap()
                notify("success", "Video deleted.")
            }
        } catch (error) {
            notify("error", errorText(error, "Could not delete that."))
        } finally {
            setPendingDelete(null)
        }
    }

    /* ------------------------------ render ----------------------------- */

    if (!canManage) {
        return (
            <div className="min-h-screen bg-slate-50 px-4 py-8">
                <div className="mx-auto max-w-3xl">
                    <EmptyState
                        icon={<SchoolOutlined />}
                        title="Owner only"
                        description="Courses are published by the platform owner."
                    />
                </div>
            </div>
        )
    }

    if (!loading && !course) {
        return (
            <div className="min-h-screen bg-slate-50 px-4 py-8">
                <div className="mx-auto max-w-3xl">
                    <EmptyState
                        icon={<SchoolOutlined />}
                        title="Course not found"
                        description="It may have been deleted."
                        actionLabel="All courses"
                        onAction={() => navigate(managePaths.root)}
                    />
                </div>
            </div>
        )
    }

    const chapterNumber = (section) =>
        section.chapter ? chapters.findIndex((c) => c.chapter_id === section.chapter.chapter_id) + 1 : null
    const activeNumber = active ? chapterNumber(active) : null
    const activeLabel = activeChapter ? `Chapter ${activeNumber}: ${activeChapter.title}` : "Not in a chapter"
    const activeVideos = active ? active.lectures : []
    const previewCount = lectures.filter((item) => item.is_preview).length

    return (
        <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 sm:py-8">
            <div className="mx-auto w-full max-w-6xl space-y-5">

                {/* ============ Course header ============ */}
                <header className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                    <Button
                        size="small"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(managePaths.root)}
                        sx={{ mb: 1.5, ml: -1, textTransform: "none" }}
                    >
                        All courses
                    </Button>

                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex min-w-0 items-start gap-3">
                            <span
                                className="h-12 w-12 shrink-0 rounded-xl"
                                style={{ background: `linear-gradient(135deg, ${(course && course.accent) || "#4f46e5"}, #0f172a)` }}
                            />
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                    {course ? course.subject_name : " "}
                                    {course && course.class_name ? ` · ${course.class_name}` : ""}
                                </p>
                                <h1 className="mt-0.5 truncate text-xl font-bold text-slate-800 sm:text-2xl">
                                    {course ? course.title : "Loading…"}
                                </h1>
                                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                                    <span className="flex items-center gap-1">
                                        <FolderOutlined sx={{ fontSize: 15 }} />
                                        {plural(chapters.length, "chapter")}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <VideoLibraryOutlined sx={{ fontSize: 15 }} />
                                        {plural(lectures.length, "video")}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <VisibilityOutlined sx={{ fontSize: 15 }} />
                                        {plural(previewCount, "free preview")}
                                    </span>
                                    {course && (
                                        <span className="font-semibold text-slate-700">
                                            {formatPrice(course.price, course.currency)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Button size="small" variant="outlined" startIcon={<EditOutlined />} onClick={() => setCourseDialogOpen(true)} disabled={!course}>
                                Edit course
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<StarOutline />} onClick={() => setReviewsOpen(true)} disabled={!course}>
                                Reviews
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<ForumOutlined />} onClick={() => navigate(managePaths.questions(course_id))}>
                                Q&A
                            </Button>
                        </div>
                    </div>
                </header>

                {/* ============ Chapters + videos ============ */}
                <div className="grid grid-cols-1 gap-5 md:grid-cols-[280px_minmax(0,1fr)]">

                    {/* Chapter rail */}
                    <aside className="h-fit rounded-2xl border border-slate-200 bg-white md:sticky md:top-4">
                        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                            <h2 className="text-sm font-semibold text-slate-800">Chapters</h2>
                            <span className="text-xs text-slate-400">{chapters.length}</span>
                        </div>

                        {/* Phones: chapters scroll sideways; larger screens: a list */}
                        <nav className="flex gap-2 overflow-x-auto p-3 md:max-h-[60vh] md:flex-col md:gap-1 md:overflow-y-auto md:overflow-x-visible">
                            {sections.length === 0 && !loading && (
                                <p className="px-1 py-2 text-xs text-slate-500">
                                    No chapters yet. Add the first one below.
                                </p>
                            )}
                            {sections.map((section, index) => {
                                const isActive = index === activeIndex
                                const number = chapterNumber(section)
                                return (
                                    <button
                                        key={sectionKey(section)}
                                        type="button"
                                        onClick={() => selectSection(sectionKey(section))}
                                        aria-current={isActive ? "true" : undefined}
                                        className={`flex min-w-[180px] shrink-0 items-center gap-3 rounded-xl px-3 py-2.5 text-left transition md:min-w-0 ${
                                            isActive
                                                ? "bg-indigo-50 ring-1 ring-indigo-200"
                                                : "hover:bg-slate-50"
                                        }`}
                                    >
                                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                                            isActive ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"
                                        }`}>
                                            {number || "–"}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className={`block truncate text-sm md:line-clamp-2 md:whitespace-normal ${isActive ? "font-semibold text-indigo-700" : "font-medium text-slate-700"}`}>
                                                {section.chapter ? section.chapter.title : "Not in a chapter"}
                                            </span>
                                            <span className="block text-xs text-slate-400">
                                                {plural(section.lectures.length, "video")}
                                            </span>
                                        </span>
                                    </button>
                                )
                            })}
                        </nav>

                        <div className="border-t border-slate-200 p-3">
                            <NewChapterField saving={createChapterState.isLoading} onCreate={addChapter} />
                        </div>
                    </aside>

                    {/* Selected chapter */}
                    <main className="min-w-0 space-y-4">
                        {!loading && sections.length === 0 ? (
                            <EmptyState
                                icon={<CreateNewFolderOutlined />}
                                title="Start with a chapter"
                                description="Split the course into chapters, such as one per unit of the syllabus, then add each chapter's videos in order. Type a chapter title in the box on the left."
                            />
                        ) : active && (
                            <>
                                <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                        <div className="min-w-0">
                                            {activeChapter && (
                                                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                                                    Chapter {activeNumber} of {chapters.length}
                                                </p>
                                            )}
                                            <h2 className="mt-0.5 text-lg font-bold text-slate-800">
                                                {activeChapter ? activeChapter.title : "Not in a chapter"}
                                            </h2>
                                            <p className="mt-1 text-sm text-slate-500">
                                                {activeChapter
                                                    ? activeChapter.description || plural(activeVideos.length, "video")
                                                    : "Videos that belong to no chapter. Students see them after every chapter; move them into one."}
                                            </p>
                                        </div>

                                        <div className="flex shrink-0 items-center justify-between gap-1 sm:justify-end">
                                            {!composerOpen && (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<Add />}
                                                    onClick={() => setComposerOpen(true)}
                                                    sx={{ whiteSpace: "nowrap" }}
                                                >
                                                    Add video
                                                </Button>
                                            )}
                                            {activeChapter && (
                                                <IconButton
                                                    aria-label="Chapter options"
                                                    onClick={(event) => setChapterMenu(event.currentTarget)}
                                                >
                                                    <MoreVert />
                                                </IconButton>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                {composerOpen && (
                                    <VideoComposer
                                        key={sectionKey(active)}
                                        chapterLabel={activeLabel}
                                        saving={createLectureState.isLoading}
                                        onAdd={addVideo}
                                        onClose={() => setComposerOpen(false)}
                                    />
                                )}

                                {activeVideos.length === 0 && !composerOpen ? (
                                    <EmptyState
                                        icon={<VideoLibraryOutlined />}
                                        title="No videos in this chapter yet"
                                        description="Paste a YouTube, Vimeo or .mp4 link to add the first one."
                                        actionLabel="Add video"
                                        onAction={() => setComposerOpen(true)}
                                    />
                                ) : (
                                    <ol className="space-y-2">
                                        {activeVideos.map((lecture, index) => (
                                            <VideoRow
                                                key={lecture.lecture_id}
                                                lecture={lecture}
                                                number={activeNumber ? `${activeNumber}.${index + 1}` : String(index + 1)}
                                                first={index === 0}
                                                last={index === activeVideos.length - 1}
                                                busy={busy}
                                                canMove={chapters.length > 0}
                                                onMoveUp={() => moveVideoWithin(index, -1)}
                                                onMoveDown={() => moveVideoWithin(index, 1)}
                                                onMoveTo={(event) => setMoveMenu({ anchor: event.currentTarget, lecture })}
                                                onEdit={() => {
                                                    setDialogError("")
                                                    setEditingLectureId(lecture.lecture_id)
                                                }}
                                                onDelete={() => setPendingDelete({ kind: "lecture", item: lecture })}
                                            />
                                        ))}
                                    </ol>
                                )}
                            </>
                        )}
                    </main>
                </div>
            </div>

            {/* Chapter options */}
            <Menu
                anchorEl={chapterMenu}
                open={Boolean(chapterMenu)}
                onClose={() => setChapterMenu(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem
                    onClick={() => {
                        setChapterMenu(null)
                        setDialogError("")
                        setChapterDialog({ chapter: activeChapter })
                    }}
                >
                    <EditOutlined fontSize="small" sx={{ mr: 1.5 }} /> Rename / describe
                </MenuItem>
                <MenuItem disabled={busy || chapterIndex <= 0} onClick={() => moveChapter(-1)}>
                    <ArrowUpward fontSize="small" sx={{ mr: 1.5 }} /> Move chapter up
                </MenuItem>
                <MenuItem disabled={busy || chapterIndex === -1 || chapterIndex >= chapters.length - 1} onClick={() => moveChapter(1)}>
                    <ArrowDownward fontSize="small" sx={{ mr: 1.5 }} /> Move chapter down
                </MenuItem>
                {/* The server refuses while the chapter still holds videos */}
                <MenuItem
                    disabled={activeVideos.length > 0}
                    onClick={() => {
                        setChapterMenu(null)
                        setPendingDelete({ kind: "chapter", item: activeChapter })
                    }}
                    sx={{ color: "error.main" }}
                >
                    <DeleteOutline fontSize="small" sx={{ mr: 1.5 }} />
                    {activeVideos.length > 0 ? "Delete chapter (move its videos out first)" : "Delete chapter"}
                </MenuItem>
            </Menu>

            {/* "Move to" menu for one video */}
            <Menu anchorEl={moveMenu ? moveMenu.anchor : null} open={Boolean(moveMenu)} onClose={() => setMoveMenu(null)}>
                <ListSubheader sx={{ lineHeight: "32px" }}>Move to</ListSubheader>
                {chapters.map((chapter, index) => (
                    <MenuItem
                        key={chapter.chapter_id}
                        dense
                        disabled={moveMenu ? moveMenu.lecture.chapter_id === chapter.chapter_id : false}
                        onClick={() => moveVideoTo(moveMenu.lecture, chapter.chapter_id)}
                    >
                        {index + 1}. {chapter.title}
                    </MenuItem>
                ))}
                <MenuItem
                    dense
                    disabled={moveMenu ? (moveMenu.lecture.chapter_id ?? null) === null : false}
                    onClick={() => moveVideoTo(moveMenu.lecture, null)}
                >
                    <em>Not in a chapter</em>
                </MenuItem>
            </Menu>

            <LectureFormDialog
                open={Boolean(editingLecture)}
                lecture={editingLecture}
                course={course}
                chapters={chapters}
                saving={editLectureState.isLoading}
                error={dialogError}
                onClose={() => setEditingLectureId(null)}
                onSubmit={submitLecture}
            />

            <ChapterFormDialog
                open={Boolean(chapterDialog)}
                chapter={chapterDialog ? chapterDialog.chapter : null}
                course={course}
                saving={editChapterState.isLoading}
                error={dialogError}
                onClose={() => setChapterDialog(null)}
                onSubmit={submitChapter}
            />

            <CourseFormDialog
                open={courseDialogOpen}
                course={course}
                saving={editCourseState.isLoading}
                onClose={() => setCourseDialogOpen(false)}
                onSubmit={submitCourse}
            />

            <CourseReviewsDialog open={reviewsOpen} course={course} onClose={() => setReviewsOpen(false)} />

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title={pendingDelete && pendingDelete.kind === "chapter" ? "Delete this chapter?" : "Delete this video?"}
                message={
                    pendingDelete
                        ? pendingDelete.kind === "chapter"
                            ? `"${pendingDelete.item.title}" has no videos and will be removed.`
                            : `"${pendingDelete.item.title}" will be removed with its files and student questions.`
                        : ""
                }
                busy={deleteChapterState.isLoading || deleteLectureState.isLoading}
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />

            {(loading || busy) && <Loader show={true} />}

            {notification.show && (
                <ToastMessage
                    {...notification}
                    onClose={() => setNotification({ show: false, type: "", message: "" })}
                />
            )}
        </div>
    )
}
